import {
  CircleCheck,
  TriangleAlert,
  CircleQuestionMark,
  Info,
  CircleMinus,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Clock,
  Circle,
  Tag,
} from 'lucide-react';
import type { ComponentType } from 'react';

// ---------------------------------------------------------------------------
// Label map — plain-English tooltip and aria-label for each status
//
// VOCABULARY IS COPIED, KEEP IN SYNC: these status strings are defined by
// the backend services (e.g. abbreviation_service.py, documented on
// AbbreviationEntry in routers/abbreviations.py) and also mapped by the Word
// add-in in chrona-addin/src/taskpane/review-states.js. A status vocabulary
// change must visit every site.
// ---------------------------------------------------------------------------

const LABEL_MAP: Record<string, Record<string, string>> = {
  abbreviations: {
    defined: 'Defined',
    undefined: 'Undefined',
    conflicting: 'Conflicting definitions',
    defined_elsewhere: 'Defined elsewhere',
    // A2F-1177: compound codes surfaced on the Abbreviations page, tagged as a
    // distinct known-entity type (not an abbreviation).
    compound_code: 'Compound code',
  },
  cross_references: {
    valid: 'Valid',
    missing: 'Missing',
  },
  dose_unit: {
    consistent: 'Consistent',
    inconsistent: 'Inconsistent',
  },
  clear_document: {
    clear: 'Clear',
    not_clear: 'Not clear',
  },
  clear_owner: {
    complete: 'Sign-off complete',
    partial: 'Partially signed off',
    none: 'No sign-off',
  },
  clear_section: {
    signed_off: 'Signed off',
    not_signed_off: 'Awaiting sign-off',
  },
  submission_classification: {
    present: 'Present',
    missing: 'Missing',
  },
};

// ---------------------------------------------------------------------------
// Icon + color resolution
// ---------------------------------------------------------------------------

interface IconConfig {
  icon: ComponentType<{ className?: string }>;
  bg: string;
  text: string;
}

const STATUS_ICONS: Record<string, IconConfig> = {
  // Green — success states
  defined:      { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  consistent:   { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  valid:        { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  clear:        { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  present:      { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  signed_off:   { icon: CircleCheck, bg: 'bg-green-50',  text: 'text-green-700' },

  // Orange — warning states
  conflicting:  { icon: TriangleAlert, bg: 'bg-orange-50', text: 'text-orange-700' },
  inconsistent: { icon: TriangleAlert, bg: 'bg-orange-50', text: 'text-orange-700' },
  discrepancy:  { icon: TriangleAlert, bg: 'bg-orange-50', text: 'text-orange-700' },
  not_clear:    { icon: TriangleAlert, bg: 'bg-amber-50',  text: 'text-amber-700' },
  missing:      { icon: TriangleAlert, bg: 'bg-orange-50', text: 'text-orange-700' },

  // Red — error states
  undefined:    { icon: CircleQuestionMark,    bg: 'bg-red-50',    text: 'text-red-700' },

  // Blue — informational
  defined_elsewhere: { icon: Info,     bg: 'bg-blue-50',   text: 'text-blue-700' },

  // Slate — neutral
  single:           { icon: CircleMinus, bg: 'bg-slate-100', text: 'text-slate-500' },

  // Indigo — A2F-1177: compound code (known entity, not an abbreviation)
  compound_code:    { icon: Tag,         bg: 'bg-indigo-50',  text: 'text-indigo-700' },

  // Shield variants (Clear — owner sign-off)
  complete:     { icon: ShieldCheck,  bg: 'bg-green-50',   text: 'text-green-700' },
  partial:      { icon: ShieldAlert,  bg: 'bg-orange-50',  text: 'text-orange-700' },
  none:         { icon: ShieldX,      bg: 'bg-red-50',     text: 'text-red-700' },

  // Clock / Circle variants
  not_signed_off:   { icon: Clock,   bg: 'bg-slate-100',  text: 'text-slate-400' },
  missing_section:  { icon: Circle,  bg: 'bg-slate-50',   text: 'text-slate-300' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  /** Status key (e.g., "defined", "inconsistent", "clear") */
  status: string;
  /** Feature key for label resolution (e.g., "abbreviations", "dose_unit") */
  feature?: string;
  /** Override: explicit label (skips LABEL_MAP lookup) */
  label?: string;
  /** Override: explicit icon component */
  icon?: ComponentType<{ className?: string }>;
  /** Override: explicit bg class */
  bg?: string;
  /** Override: explicit text color class */
  textColor?: string;
  /** Icon size class (default: "w-3 h-3") */
  iconSize?: string;
  /** Render as pill badge (default) or icon-only */
  pill?: boolean;
}

export function StatusBadge({
  status,
  feature,
  label: labelOverride,
  icon: iconOverride,
  bg: bgOverride,
  textColor: textOverride,
  iconSize = 'w-3 h-3',
  pill = true,
}: StatusBadgeProps) {
  // Resolve icon config
  const config = STATUS_ICONS[status];
  const Icon = iconOverride ?? config?.icon ?? CircleQuestionMark;
  const bg = bgOverride ?? config?.bg ?? 'bg-slate-100';
  const textColor = textOverride ?? config?.text ?? 'text-slate-500';

  // Resolve label
  const resolvedLabel =
    labelOverride ??
    (feature && LABEL_MAP[feature]?.[status]) ??
    status.replace(/_/g, ' ');

  if (!pill) {
    // Icon-only mode (no pill background)
    return (
      <span
        className={`inline-flex items-center ${textColor}`}
        aria-label={resolvedLabel}
        title={resolvedLabel}
      >
        <Icon className={iconSize} />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${bg} ${textColor} text-xs font-medium`}
      aria-label={resolvedLabel}
      title={resolvedLabel}
    >
      <Icon className={iconSize} />
    </span>
  );
}
